/**
 * useDocumentActions Hook
 * Shared logic for document preview and download across all components.
 *
 * Preview: fetches a signed/local URL via /files/:id/preview, tracks state
 *          for rendering in DocumentPreviewDialog.
 * Download: fetches a signed/local URL via /files/:id/download?json=1,
 *           opens it in a new tab.
 */

import { useState, useCallback } from 'react';
import apiClient from '../utils/apiClient';

/**
 * Resolve a URL returned by the files API.
 * Blob-storage URLs are already absolute (https://…); local fallback URLs
 * are relative (/api/files/…) and need the current origin prepended.
 */
const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // Strip /api suffix from baseURL so we don't double it
  const base = apiClient.defaults.baseURL?.replace(/\/api$/, '') || window.location.origin;
  return `${base}${url}`;
};

export default function useDocumentActions() {
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  /**
   * Open document in the in-page preview dialog.
   * Calls GET /files/:id/preview which returns { url, contentType }.
   */
  const openPreview = useCallback(async (docId) => {
    if (!docId) return;
    try {
      const response = await apiClient.get(`/files/${docId}/preview`);
      const { url, contentType } = response.data.data;
      setPreviewUrl(resolveUrl(url));
      setPreviewType(contentType || 'application/octet-stream');
      setPreviewDocId(docId);
    } catch (err) {
      console.error('Error loading document preview:', err);
    }
  }, []);

  /**
   * Close the preview dialog and clean up state.
   */
  const closePreview = useCallback(() => {
    setPreviewUrl(null);
    setPreviewType(null);
    setPreviewDocId(null);
  }, []);

  /**
   * Download a document. Fetches a signed/local URL via the API then opens
   * it in a new browser tab so the browser handles the save-as flow.
   */
  const downloadDocument = useCallback(async (docId) => {
    if (!docId) return;
    try {
      const response = await apiClient.get(`/files/${docId}/download?json=1`);
      const { url } = response.data.data;
      window.open(resolveUrl(url), '_blank');
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  }, []);

  return {
    // State for DocumentPreviewDialog
    previewDocId,
    previewUrl,
    previewType,
    previewOpen: !!previewUrl,
    // Actions
    openPreview,
    closePreview,
    downloadDocument,
  };
}
